import type { CloudMe, DiscoveryDocument, OrganisationSummary } from '@aioe/shared';
import { createContext, useContext } from 'react';

/** Mojo Up AI Cloud only: the person, their organisations and the one the console acts in. */
export interface CloudState {
  me: CloudMe;
  /** The active organisation: every API request names it. */
  organisation: OrganisationSummary;
  /** Makes another organisation active; the data views load again for it. */
  switchTo(id: string): void;
  /** Reads the person and their organisations again (after a rename, a join, a leave). */
  reload(): Promise<CloudMe>;
}

export interface AuthState {
  ready: boolean;
  error?: string;
  discovery?: DiscoveryDocument;
  /** `id` is the person's object id: what a record's `by.principal` names. */
  /** `picture`: the person's profile picture (an object URL on Enterprise, Cloud's URL), when the provider has one. */
  account?: { name?: string; email?: string; id?: string; picture?: string };
  /** Nobody is signed in in this tab, so the sign-in page (or, after signing out, the signed-out page) shows. */
  signedOut?: boolean;
  /** This tab has just signed out ('idle': the console signed itself out): the signed-out page shows, wherever the address points. */
  ended?: 'manual' | 'idle';
  /** Why the console could not start: `discovery` when this address names no organisation. */
  failure?: 'discovery';
  cloud?: CloudState;
  /** Enterprise: starts the identity provider's sign-in (Cloud signs in on its own page). */
  signIn?(): void;
  /** Enterprise: the sign-in could not be renewed silently; the renewal dialog asks the person to continue. */
  renewal?: { renew(): Promise<void> };
  /**
   * Step-up (Enterprise): the API answered `sign_in_again`, so the person proves who they are again
   * before taking control. Resolves once they have; rejects when they choose not to. Absent where the
   * console cannot ask (Cloud, a development token): the caller shows the API's sentence instead.
   */
  signInAgain?(message: string): Promise<void>;
  /** The step-up request the renewal dialog shows while one waits. */
  stepUp?: { message: string; confirm(): Promise<void>; cancel(): void };
  /** `idle`: the console signed the person out after a long idle spell, and the signed-out page says so. */
  signOut(opts?: { idle?: boolean }): void;
}

/** What a request says while the sign-in waits to be renewed. */
export const RENEWAL_MESSAGE = 'Your sign-in needs renewing. Choose Continue in the dialog; nothing you have typed is lost.';
/** Fired on window once a renewal succeeds without leaving the page: views that failed load again. */
export const RENEWED_EVENT = 'aioe:sign-in-renewed';

/** The page a signed-out person lands on, in both editions: it never starts a sign-in by itself. */
export const SIGNED_OUT_PATH = '/signed-out';

export const AuthContext = createContext<AuthState>({ ready: false, signOut() {} });
export const useAuth = () => useContext(AuthContext);
/** The Cloud state, or undefined on Enterprise (and while signed out). */
export const useCloud = () => useContext(AuthContext).cloud;
